import { ErrorProps } from '@/types';
import { useSession } from '@context/sessionProvider';
import useSWR from 'swr';
import { CategoryItem } from '@/types';
import type { ConfigData } from '@/types';
import { PaginationEnum } from '@components/custom/gallery/paginationEnum';

type CategoriesRes = {
	data: {
		total: number;
		category_tree: CategoryItem[];
	};
};

export type GridConfig = ConfigData & {
	id: number;
};

export type GridResponse = {
	data?: GridConfig[];
	message?: string;
};

export type DefaultBackground = {
	id: number;
	name: string;
	image_path: string;
};

export type DefaultBackgroundRes = {
	data: DefaultBackground[];
	total: number;
};

type FetcherKey = [string] | [string, Record<string, string | number | undefined>];

export const createFetcher = (token: string) => async (queryParams: FetcherKey) => {
	const headers = new Headers({
		'Content-Type': 'application/json',
	});

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const rawParams = queryParams[1] ?? {};
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(rawParams)) {
		if (value !== undefined) {
			params[key] = String(value);
		}
	}

	const query = new URLSearchParams(params).toString();
	const fullUrl = query ? `${queryParams[0]}?${query}` : queryParams[0];

	const options: RequestInit = {
		method: 'GET',
		headers: headers,
	};

	const res = await fetch(fullUrl, options);

	if (!res.ok) {
		const { message } = await res.json();
		const error: ErrorProps = new Error(message || 'An error occurred while fetching the data.');
		error.status = res.status;
		throw error;
	}

	return res.json();
};

export function useConfigs() {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () => ['/api/config/get_all'];
	const { data, error, isLoading, mutate } = useSWR<GridResponse>(getKey, fetcherWithToken, {
		refreshInterval: 0,
	});

	return {
		configs: data?.data,
		error,
		isLoading,
		mutate,
	};
}

export function useCategories() {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () => ['/api/categories'];

	const { data, error, isLoading } = useSWR<CategoriesRes>(getKey, fetcherWithToken, {
		refreshInterval: 0,
	});

	return {
		data: data?.data,
		error,
		isLoading,
	};
}

export function useGallery(page = PaginationEnum.page, limit = PaginationEnum.limit) {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () => ['/api/backgrounds/default', { page, limit }];

	const { data, error, isLoading, mutate } = useSWR<DefaultBackgroundRes>(getKey, fetcherWithToken, {
		refreshInterval: 0,
		keepPreviousData: true,
		dedupingInterval: 5000,
	});

	return {
		data: data?.data || [],
		total: data?.total || 0,
		error,
		isLoading,
		mutate,
	};
}
